import React, { useEffect, useMemo, useState } from "react";
import { SchedulePreviewVisitRail } from "../schedule/SchedulePreviewPanel";
import { buildSchedulePreviewReport } from "../schedule/schedulePreviewChecklist";
import { buildWizardPatchFromCohort, fetchCohortSetup } from "../schedule/scheduleService";
import { defaultWizardState, GROUP_TYPE, SETUP_TOGGLES } from "../schedule/scheduleWorkflow";
import {
  buildMultiGroupVisitCohorts,
  cohortVisitGroups,
  treatmentGroupNames,
} from "../schedule/scheduleVisitDraft";
import "../schedule/ScheduleEditor.scss";
import { getStudyScheduleId } from "./studyMgmtReportUtils";
import "./ScheduleClonePreview.scss";

function YesNoValue({ enabled }) {
  return <strong>{enabled ? "Yes" : "No"}</strong>;
}

export default function ScheduleClonePreview({
  sourceStudy,
  organizationId,
  createdBy = "",
}) {
  const [cohort, setCohort] = useState(null);
  const [wizard, setWizard] = useState(() => defaultWizardState());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const scheduleId = getStudyScheduleId(sourceStudy);

  useEffect(() => {
    if (!sourceStudy || !scheduleId || !organizationId) {
      setCohort(null);
      setWizard(defaultWizardState());
      setReport(null);
      setError("");
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      setReport(null);
      try {
        const parsed = await fetchCohortSetup({
          studyId: scheduleId,
          organizationId,
        });
        if (cancelled) return;
        const baseWizard = defaultWizardState();
        const nextWizard = {
          ...baseWizard,
          ...buildWizardPatchFromCohort(parsed, baseWizard),
        };
        setCohort(parsed);
        setWizard(nextWizard);

        const visitBuilderMode =
          nextWizard.groupType === GROUP_TYPE.MULTIPLE ? "cohorts" : "single";
        const visitCohorts =
          nextWizard.groupType === GROUP_TYPE.MULTIPLE
            ? buildMultiGroupVisitCohorts({
                groupNames: nextWizard.groupNames,
                groupVisitDrafts: nextWizard.groupVisitDrafts,
                visitDrafts: nextWizard.visitDrafts,
                apiVisits: parsed.visits || [],
                studyScreening: nextWizard.setupFlags.studyScreening,
                allGroupsSameSchedule: nextWizard.allGroupsSameSchedule,
              })
            : cohortVisitGroups(
                nextWizard.groupNames,
                nextWizard.groupVisitDrafts,
                nextWizard.setupFlags.studyScreening,
              );

        const nextReport = await buildSchedulePreviewReport({
          wizard: nextWizard,
          cohort: parsed,
          scheduleCreated: !!parsed.isScheduleDone,
          visitBuilderMode,
          visitCohorts,
          studyScheduleId: parsed.studyScheduleId || scheduleId,
          organizationId,
          createdBy,
        });
        if (!cancelled) setReport(nextReport);
      } catch (err) {
        if (cancelled) return;
        setCohort(null);
        setReport(null);
        setError(err.message || "Could not load schedule preview.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [createdBy, organizationId, scheduleId, sourceStudy]);

  const setupFlags = useMemo(
    () =>
      SETUP_TOGGLES.filter((toggle) => {
        if (toggle.dependsOn && !wizard.setupFlags[toggle.dependsOn]) return false;
        return true;
      }),
    [wizard.setupFlags],
  );

  const enabledOptionalLabels = useMemo(
    () =>
      SETUP_TOGGLES.filter((toggle) => {
        if (toggle.dependsOn && !wizard.setupFlags[toggle.dependsOn]) return false;
        return !!wizard.setupFlags[toggle.key];
      }).map((toggle) => toggle.label),
    [wizard.setupFlags],
  );

  const visitBuilderMode =
    wizard.groupType === GROUP_TYPE.MULTIPLE ? "cohorts" : "single";

  const showGroupHeaders =
    wizard.groupType === GROUP_TYPE.MULTIPLE && visitBuilderMode === "cohorts";

  const treatmentGroups = treatmentGroupNames(wizard.groupNames || []);

  if (!sourceStudy) {
    return (
      <div className="schedule-clone-preview__empty">
        <i className="fas fa-calendar-alt" aria-hidden />
        <p>Select a study on the left to preview its schedule.</p>
      </div>
    );
  }

  if (loading && !report) {
    return (
      <div className="schedule-clone-preview__loading">
        <div className="spinner-border spinner-border-sm text-primary" role="status">
          <span className="visually-hidden">Loading preview…</span>
        </div>
        <span>Loading schedule preview…</span>
      </div>
    );
  }

  if (error && !report) {
    return <div className="schedule-clone-preview__error">{error}</div>;
  }

  return (
    <div className="schedule-clone-preview">
      <section className="schedule-clone-preview__card schedule-clone-preview__card--setup">
        <h6 className="schedule-clone-preview__card-title">Setup &amp; structure</h6>
        <ul className="schedule-clone-preview__setup-list">
          <li>
            <span>Group type</span>
            <strong>{wizard.groupType}</strong>
          </li>
          {wizard.groupType === GROUP_TYPE.MULTIPLE ? (
            <li>
              <span>Treatment groups</span>
              <strong>{treatmentGroups.length}</strong>
            </li>
          ) : null}
          {setupFlags.map((toggle) => (
            <li key={toggle.key}>
              <span>{toggle.label}</span>
              <YesNoValue enabled={!!wizard.setupFlags[toggle.key]} />
            </li>
          ))}
        </ul>
      </section>

      {report ? (
        <div className="schedule-clone-preview__visit-rail">
          <SchedulePreviewVisitRail
            visitGroups={report.visitGroups}
            showGroupHeaders={showGroupHeaders}
            totalVisits={report.visitReports.length}
            enabledOptionalLabels={enabledOptionalLabels}
            expandAllVisits
            showValueOnComplete
          />
        </div>
      ) : null}
    </div>
  );
}
